import { useCallback, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import CaseIcon from "../CaseIcon";
import {focusNextInputElementInJrEditMode} from './util';

// Input field for the case card
function CaseInput({ index, value, onChange, attributeName, Dyn, rawValue, onBlur }) {
    const handleKeyDown = (e) => {
        // Focus next element
        if (e.key === 'Enter') {
            e.preventDefault();
            focusNextInputElementInJrEditMode(Dyn, e.target);
        }
    };

    const handleBlur = (e) => {
        if (onBlur) {
            onBlur(index, e.target.value);
        }
    };

    return (
        <div className="tde-row tde-mb-1 tde-py-2 tde-d-flex tde-flex-items-center"
        style={{borderBottom: '1px solid #C1C8EB'}}>
            <label className="tde-flex-1">{attributeName}</label>
            <input
                type="text"
                className="tde-input tde-input-text tde-flex-1"
                value={rawValue !== undefined ? rawValue : (value != null ? String(value) : "")}
                onChange={(e) => onChange(index, e.target.value)}
                onBlur={handleBlur}
                style={{ height: 42 }}
                onKeyDown={handleKeyDown}
            ></input>
        </div>
    );
}

// Get the row/case values for the give caseNo
function _getCaseValues(Dyn, caseNo) {
    // NOTE: when editing, ignore Attribute.isVisible flag, ignore CaseFilter.caseIsFiltered,
    //  hide attribute with Attribute.isCaseAttribute; compare to CaseTableComponent.getBaseTable()
    const rowNumber = caseNo - 1; // caseNo is 1-based; rowNumber is 0-based CONSIDER making caseNo 0-based, and add 1 when displaying
    const editing = true;
    const showAttribute = (attr) => (attr.isVisible || editing) && !attr.isCaseAttribute;
    const caseValueFormat = { rawNumbers: true, formatted: false };
    const records = Dyn.dataSet.getTableRowData(rowNumber, caseValueFormat)
        .filter((col,index) => showAttribute( Dyn.dataSet.attribs[index]));
    // console.log("Row Data:", rowNumber, records);
    return records;
}

// Wrapper for Case Card React Element
let caseEditTimeout = null;
const CaseCard = forwardRef(({ Dyn, caseIndex = 1, onChange }, ref) => {
    const numberOfAttributes = Dyn.dataSet.getNumAttributes() - 1;
    const [caseNo, setCaseNo] = useState(caseIndex);
    const [caseValues, setCaseValues] = useState(_getCaseValues(Dyn, caseNo));
    const [rawInputValues, setRawInputValues] = useState({}); // Store raw user input while editing

    useImperativeHandle(ref, () => ({
        updateCaseNo: (caseNo) => {
            if(caseNo <= CaseCard.MAX_CASES) {
                setCaseNo(caseNo);
            }
        }
    }));

    // Save the case data
    const onSave = useCallback((caseValues) => {
        // Save the attributes
        const constructedData = caseValues.map((data, i) => {
            const row = caseNo;
            const col = i + 1;
            return [row, col, null, data];
        });
        // Update dataset
        Dyn.dataSet.updateCaseTableValues(constructedData, true);
        // Notify parent to update the case card
        onChange();
    }, [caseNo, Dyn]);

    const onValChange = useCallback((index, value) => {
        // Store raw input value to preserve user formatting
        setRawInputValues(prev => ({
            ...prev,
            [index]: value
        }));

        // Clear existing timeout
        if(caseEditTimeout) {
            clearTimeout(caseEditTimeout);
        }

        // Set timeout for auto-save (but keep raw input visible)
        caseEditTimeout = setTimeout(() => {
            // Update the actual case values and save (but don't clear raw input)
            const newCaseValues = [...caseValues];
            newCaseValues[index] = value;
            setCaseValues(newCaseValues);

            // DON'T clear raw input here - let user keep typing
            // Raw input will only be cleared on blur

            // console.log("Auto-saving case data (keeping raw input visible)..");
            onSave(newCaseValues);
        }, 1500);
    }, [caseValues, onSave]);

    const onInputBlur = useCallback((index, value) => {
        // Clear timeout and immediately save on blur
        if(caseEditTimeout) {
            clearTimeout(caseEditTimeout);
        }

        // Update the actual case values and save immediately
        const newCaseValues = [...caseValues];
        newCaseValues[index] = value;
        setCaseValues(newCaseValues);

        // Clear raw input for this field since it's now saved
        setRawInputValues(prev => {
            const newRaw = {...prev};
            delete newRaw[index];
            return newRaw;
        });

        // console.log("Saving case data on blur..");
        onSave(newCaseValues);
    }, [caseValues, onSave]);

    // Delete the case data
    const onDelete = useCallback(() => {
        // Remove the case from the dataset; sends "records:setAttributeValues" notification
        const row = caseNo-1, deleteCount = 1;
        Dyn.dataSet.spliceCaseTableRows( row, deleteCount );

        // Notify parent to update the case card
        onChange();
    }, [caseNo]);

    // Listen for events
    useEffect(() => {
        setCaseValues(_getCaseValues(Dyn, caseNo));
        setRawInputValues({}); // Clear raw inputs when case changes
        const subscriptions = [];
        Dyn.mediatorLocalSubscribe(
            subscriptions,
            "records:setAddedAttribute",
            (data) => {
                setCaseValues(_getCaseValues(Dyn, caseNo));
                setRawInputValues({}); // Clear raw inputs
            }
        );
        Dyn.mediatorLocalSubscribe(
            subscriptions,
            "records:setRemovedAttribute",
            (data) => {
                setCaseValues(_getCaseValues(Dyn, caseNo));
                setRawInputValues({}); // Clear raw inputs
            }
        );
        Dyn.mediatorLocalSubscribe(
            subscriptions,
            "records:setAttributeValues",
            (data) => {
                setCaseValues(_getCaseValues(Dyn, caseNo));
                // Don't clear raw inputs here - this event fires after our save
            }
        );
        return () => {
            Dyn.mediatorLocalRemoveAll(subscriptions);
        };
    }, [Dyn, caseNo]);
    return (
        <div
            className="tde-elem-case-editor"
            style={{
                height: CaseCard.height,
                marginLeft: CaseCard.leftMargin,
                width:
                    CaseCard.width - CaseCard.leftMargin - CaseCard.rightMargin,
                position: "relative",
            }}
        >
            <div className="tde-d-flex tde-flex-items-center">
                <div>
                    <CaseIcon Dyn={Dyn} />
                </div>
                <h2 className="tde-m-0 tde-flex-1 tde-text-center">
                    Card {caseNo}
                </h2>
                <div style={{width: 34}}>
                    {caseValues.length > 0 &&
                    (<button className="tde-delete-btn" onClick={onDelete} style={{width: 34, height: 34}}>
                        <i className="tde-i tde-i-trash"></i>
                    </button>)}
                </div>
            </div>

            <div className="tde-pt-2 tde-px-3">
                {Array.from({ length: numberOfAttributes }, (v, i) => (
                    <CaseInput
                        key={i}
                        attributeName={Dyn.dataSet.attribs[i + 1].name}
                        value={caseValues[i]}
                        rawValue={rawInputValues[i]}
                        index={i}
                        onChange={onValChange}
                        onBlur={onInputBlur}
                        Dyn={Dyn}
                    />
                ))}
                {numberOfAttributes === 0 &&
                    // pace holder for the case card
                    Array.from({ length: 4 }, (v, i) => (
                        <div style={{borderBottom: '1px solid #C1C8EB', height: 50}}>
                        </div>
                    ))}
            </div>
            {/* {numberOfAttributes > 0 && (
                <div className="tde-text-center" style={{ position: "absolute", bottom: -75, left: 0, right: 0}}>
                    <button
                        className="tde-elem-btn tde-btn-primary tde-m-2"
                        onClick={onSave}
                    >
                        Save Data
                    </button>
                </div>
            )} */}
            <button
                className="tde-plain-btn tde-m-2"
                style={{ position: "absolute", right: "102%", top: '40%' }}
                disabled={caseNo <= 1}
                onClick={(e) => {
                    setCaseNo(caseNo - 1);
                }}
            >
                <i
                    className="tde-i tde-i-next"
                    style={{ transform: "rotate(180deg)" }}
                ></i>
            </button>
            <button
                className="tde-plain-btn tde-m-2"
                style={{ position: "absolute", left: "102%", top: '40%' }}
                disabled={numberOfAttributes === 0 || caseNo >= CaseCard.MAX_CASES}
                onClick={(e) => {
                    setCaseNo(caseNo + 1);
                }}
            >
                <i className="tde-i tde-i-next"></i>
            </button>
        </div>
    );
})
CaseCard.leftMargin = 40;
CaseCard.rightMargin = 40;
CaseCard.width = 300 + CaseCard.leftMargin + CaseCard.rightMargin;
CaseCard.height = 380;
CaseCard.MAX_CASES = 25;

export default CaseCard;
